/**
 * The row's actions.
 *
 * NOTHING IS OPTIMISTIC. Every one of these can be refused by a rule the browser does not hold —
 * somebody moved the article mid-flight (409 WRONG_FROM_STATE), staff did the archiving (403
 * ARCHIVE_ACTOR_MISMATCH), go-live passed while the author was deciding (409
 * GO_LIVE_ALREADY_PASSED), a verdict was recorded (409 VERDICT_ALREADY_RECORDED). So the row waits
 * for the answer and the list is re-read afterwards. Painting the new status first is how an
 * author sees their article change state twice for one click.
 *
 * THE ANSWER IS A `TransitionResult`, NOT A ROW. It carries the move — `fromStatus`, `status`,
 * `seq`, `occurredAt` — and no title and no cover, so there is nothing to patch in place from it
 * even if we wanted to.
 */
import { Button, Tooltip } from '@mantine/core';
import { ConfirmAction } from '../../features/cabinet/components';
import { useTranslation } from '../../i18n';
import { availableActions } from './article-actions';
import type { ArticleActionKind } from './article-actions';
import type { ArticleListItem } from '../../features/cabinet/api';

export interface ArticleRowActionsProps {
  article: ArticleListItem;
  busyKind: ArticleActionKind | undefined;
  onRun: (kind: ArticleActionKind, article: ArticleListItem) => Promise<void>;
}

export function ArticleRowActions({ article, busyKind, onRun }: ArticleRowActionsProps) {
  const { t } = useTranslation('articles');
  const actions = availableActions(article).filter((definition) => definition.kind !== 'archive');

  if (article.status === 'unpublished') {
    // `unpublished` reaches here: an editor took it down and only an editor can put it back. Say
    // that rather than showing an empty control, which reads as a bug.
    return (
      <Tooltip label={t('list.rowActions.unpublishedTip')}>
        <Button
          size="compact-sm"
          variant="subtle"
          data-disabled
          onClick={(event) => {
            event.preventDefault();
          }}
        >
          {t('list.rowActions.nothingToDo')}
        </Button>
      </Tooltip>
    );
  }

  return (
    <>
      {actions.map((definition) => {
        const busy = busyKind === definition.kind;
        if (definition.confirm === undefined) {
          return (
            <Tooltip key={definition.kind} label={t(definition.hint)}>
              <Button
                size="compact-sm"
                radius="xl"
                variant="default"
                loading={busy}
                onClick={() => {
                  void onRun(definition.kind, article);
                }}
              >
                {t(definition.label)}
              </Button>
            </Tooltip>
          );
        }

        return (
          <ConfirmAction
            key={definition.kind}
            title={t(definition.confirm.title)}
            body={t(definition.confirm.body)}
            confirmLabel={t(definition.label)}
            destructive={definition.destructive}
            busy={busy}
            onConfirm={async () => {
              await onRun(definition.kind, article);
            }}
            trigger={(open) => (
              <Tooltip label={t(definition.hint)}>
                <Button
                  size="compact-sm"
                  radius="xl"
                  variant={definition.destructive ? 'subtle' : 'default'}
                  loading={busy}
                  onClick={open}
                  {...(definition.destructive
                    ? { styles: { root: { color: 'oklch(var(--mx-danger))' } } }
                    : {})}
                >
                  {t(definition.label)}
                </Button>
              </Tooltip>
            )}
          />
        );
      })}
    </>
  );
}

/*
 * ★ DESIGN 15 (the owner's live look at real cabinets, 2026-10-01): the actions sit at the right of
 * the article's card, one under another as the prototype stacks them, and in a row on a phone. So
 * they are handed back as siblings rather than inside a `Group` of their own: the card's actions
 * column (`ItemCard`, ../../features/cabinet/components/kit.tsx) lays them out. The order is
 * ./article-actions.ts's, which puts the way forward first and Delete last. The pill radius is the
 * prototype's buttons'. Written at the foot so that the lines above, cited by number
 * (web/ui/src/theme/global.css cites :37-40), stay where they are.
 *
 * ★ NO "ARCHIVE" ON THE CARD (design 24, T2; the owner, 2026-10-11: "hide the archive button on the
 * article list and leave it somewhere at the bottom of the article details"). It is a "just in case"
 * action MoonX does not want used, so it sits at the very foot of a published article's own page
 * (./editor/ArchiveThisArticle.tsx), which reads its words from the same `archive` entry. A
 * published card therefore draws no action at all, and "Nothing to do here" is said only for a
 * taken-down article - not for every card left with nothing, which would now be wrong for a
 * published one. "Unarchive" stays on the card: putting an article back is not discouraged.
 */
